import React, { useEffect, useRef, useState } from "react";
import { Button, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import Select from "react-select";
import { useHistory } from "react-router-dom";
import notifications from "../../common/Components/Notifications/Notification";
import {
  delinkProductFamily,
  fetchProductFamily,
  linkProductFamily,
} from "../../services/hkpNfcTagApi";
import { getApi } from "../../helpers/auth_helpers";
import "./ProductFamilyBar.scss";
import { pickProductListThumbUrl } from "./previewMedia";

const familyLabel = (item: any) => {
  const sn = item?.orgSrNo != null ? `SN ${item.orgSrNo}` : "SN —";
  const color = item?.color || "no colour";
  const name = item?.name || "product";
  return `${sn} · ${color} · ${name}`;
};

const pickProductImage = (item: any): string => {
  const media = item?.thumbnailDetails?.media;
  if (Array.isArray(media) && media.length) {
    return pickProductListThumbUrl(media);
  }
  if (typeof item?.thumbnail === "string" && item.thumbnail.trim()) {
    return item.thumbnail;
  }
  return "";
};

type ProductLinkOption = {
  value: string;
  label: string;
  name: string;
  color: string;
  orgSrNo?: number | string;
  image: string;
  product: any;
};

const toLinkOption = (item: any): ProductLinkOption | null => {
  if (!item?.productUniqueId) {
    return null;
  }
  const name = item?.name || "product";
  return {
    value: item.productUniqueId,
    label: name,
    name,
    color: item?.color || "no colour",
    orgSrNo: item?.orgSrNo,
    image: pickProductImage(item),
    product: item,
  };
};

const ProductFamilyBar = (props: {
  parentId?: string;
  productUniqueId?: string;
  orgSrNo?: string | number;
  mode: "view" | "edit";
  onChanged?: () => void;
}) => {
  const history = useHistory();
  const { parentId, productUniqueId, orgSrNo, mode, onChanged } = props;
  const [family, setFamily] = useState<any[]>([]);
  const [linkOpen, setLinkOpen] = useState(false);
  const [options, setOptions] = useState<ProductLinkOption[]>([]);
  const [selected, setSelected] = useState<ProductLinkOption | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const requestSeq = useRef(0);
  const searchTimer = useRef<number | null>(null);

  const loadFamily = () => {
    if (!parentId) {
      setFamily([]);
      return;
    }
    fetchProductFamily(parentId).then((rows) => setFamily(Array.isArray(rows) ? rows : []));
  };

  useEffect(() => {
    loadFamily();
  }, [parentId, productUniqueId]);

  const siblings = family.filter((item) => item?.productUniqueId !== productUniqueId);
  const hasSiblings = siblings.length > 0;

  const goTo = (id: string) => {
    if (!id || id === productUniqueId) {
      return;
    }
    const path = mode === "edit" ? `/edit-product/${id}` : `/product/${id}`;
    history.push(path);
  };

  const searchProducts = async (q: string) => {
    const seq = ++requestSeq.current;
    setLoading(true);
    const params = new URLSearchParams({
      offset: "0",
      limit: "20",
      includeAiSuggestions: "false",
    });
    const query = q.trim();
    if (query) {
      params.set("name", query);
      params.set("q", query);
    }
    const url = `${process.env.REACT_APP_SERVERURL || ""}/product/products?${params.toString()}`;
    try {
      const json: any = await getApi(url);
      if (seq !== requestSeq.current) {
        return;
      }
      const docs = Array.isArray(json?.data?.docs)
        ? json.data.docs
        : Array.isArray(json?.docs)
        ? json.docs
        : [];
      setOptions(
        docs
          .filter((item: any) => item?.productUniqueId && item.productUniqueId !== productUniqueId)
          .map(toLinkOption)
          .filter((item: ProductLinkOption | null): item is ProductLinkOption => Boolean(item))
      );
    } catch {
      if (seq === requestSeq.current) {
        setOptions([]);
      }
    } finally {
      if (seq === requestSeq.current) {
        setLoading(false);
      }
    }
  };

  const closeLinkModal = () => {
    setLinkOpen(false);
    setSelected(null);
    setOptions([]);
    if (searchTimer.current) {
      window.clearTimeout(searchTimer.current);
    }
  };

  const openLinkModal = () => {
    setLinkOpen(true);
    setSelected(null);
    searchProducts("");
  };

  const confirmDelink = async () => {
    if (!productUniqueId) return;
    if (
      !window.confirm(
        "This piece will no longer show with the other variants. SN, HKP tag number, and HKP ID stay the same."
      )
    ) {
      return;
    }
    setBusy(true);
    const res = await delinkProductFamily(productUniqueId);
    setBusy(false);
    notifications({
      title: "Family",
      message: res.message || (res.success ? "Removed from family" : "Could not remove from family"),
      type: res.success ? "success" : "danger",
    });
    if (res.success) {
      loadFamily();
      onChanged?.();
    }
  };

  const confirmLink = async () => {
    const targetId = selected?.value;
    if (!productUniqueId || !targetId) return;
    const thisSn = orgSrNo != null && orgSrNo !== "" ? orgSrNo : "this piece";
    const targetSn = selected?.orgSrNo != null ? selected.orgSrNo : "that piece";
    if (
      !window.confirm(
        `Link SN ${thisSn} to the family of SN ${targetSn}? Identifiers stay unique; only the family grouping changes.`
      )
    ) {
      return;
    }
    setBusy(true);
    const res = await linkProductFamily(productUniqueId, targetId);
    setBusy(false);
    notifications({
      title: "Family",
      message: res.message || (res.success ? "Linked as variant family" : "Could not link"),
      type: res.success ? "success" : "danger",
    });
    if (res.success) {
      closeLinkModal();
      loadFamily();
      onChanged?.();
    }
  };

  return (
    <>
    <div className="hkp-family-header">
      <span className="hkp-family-header__label">Family</span>
      {hasSiblings ? (
        <Input
          type="select"
          className="hkp-family-header__select"
          value={productUniqueId || ""}
          onChange={(e) => goTo(e.target.value)}
        >
          {family.map((item) => (
            <option key={item.productUniqueId} value={item.productUniqueId}>
              {familyLabel(item)}
              {item.productUniqueId === productUniqueId ? " (current)" : ""}
            </option>
          ))}
        </Input>
      ) : null}
      <div className="hkp-family-header__actions">
        <Button color="secondary" size="sm" disabled={busy} onClick={openLinkModal}>
          Link
        </Button>
        {hasSiblings ? (
          <Button color="secondary" size="sm" disabled={busy} onClick={confirmDelink}>
            Remove
          </Button>
        ) : null}
      </div>
    </div>
      <Modal isOpen={linkOpen} toggle={closeLinkModal} fade={false} className="hkp-family-link-modal">
        <ModalHeader toggle={closeLinkModal}>Link as variant of…</ModalHeader>
        <ModalBody>
          <Label>Product</Label>
          <Select
            className="React_selector hkp-family-link-select"
            classNamePrefix="react-select"
            placeholder="Search by name, SN, or colour"
            isSearchable
            isClearable
            isLoading={loading}
            filterOption={() => true}
            options={options}
            value={selected}
            noOptionsMessage={() =>
              loading ? "Searching…" : "No matching products"
            }
            onInputChange={(value, meta) => {
              if (meta.action !== "input-change") {
                return;
              }
              if (searchTimer.current) {
                window.clearTimeout(searchTimer.current);
              }
              searchTimer.current = window.setTimeout(() => {
                searchProducts(value.trim());
              }, 300);
            }}
            onChange={(option) => setSelected((option as ProductLinkOption | null) || null)}
            menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
            menuPosition="fixed"
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 2000 }),
            }}
            formatOptionLabel={(option, { context }) => (
              <div className={`hkp-family-link-option ${context === "value" ? "is-value" : ""}`}>
                {option.image ? (
                  <img src={option.image} alt="" className="hkp-family-link-option__pic" />
                ) : (
                  <span className="hkp-family-link-option__pic is-empty" aria-hidden>
                    {(option.name || "?").charAt(0).toUpperCase()}
                  </span>
                )}
                <span className="hkp-family-link-option__text">
                  <span className="hkp-family-link-option__name">{option.name}</span>
                  <span className="hkp-family-link-option__meta">
                    {option.orgSrNo != null ? `SN ${option.orgSrNo}` : "SN —"}
                    {" · "}
                    {option.color}
                  </span>
                </span>
              </div>
            )}
          />
          <p className="hkp-family-link-hint">
            Search and pick the product this piece belongs with. Identifiers stay unique.
          </p>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={closeLinkModal}>
            Cancel
          </Button>
          <Button color="primary" disabled={busy || !selected} onClick={confirmLink}>
            Link
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default ProductFamilyBar;
